import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { CloseOutlined, InfoCircleOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { Modal, Tooltip } from 'antd';
import SignatureCanvas from 'react-signature-canvas';
import { updateUserInfo, userInfo } from '../../redux/slice';
import ExitBanner from '../../components/page/exitBanner/index.js';
import SubmitSuccessPopUp from '../../components/page/submitSuccessPopUp/index.js';
import Loading from '../../components/common/loading.js';
import { get, postForm } from '../../util/request';
import './index.scss';

const SHOW_SUPPORTING_DOCUMENTS = true;

const SUPPORTING_DOCUMENTS_INFO = {
  annualIncome: {
    title: 'Valid Supporting Documents (Issued within the last 3 months)',
    items: [
      'Latest payslip(s)',
      'Latest IRAS Notice of Assessment or equivalent tax assessment',
      'Employment contract stating position and annual remuneration',
    ],
  },
  financialAssets: {
    title: 'Valid Supporting Documents (Issued within the last 3 months)',
    items: [
      'Bank statements',
      'Fixed deposit statements',
      'CDP (Central Depository (Pte) Limited) statements',
      'Brokerage or custodian statements',
      'Managed investment portfolio statements',
      'Insurance policy statements showing surrender/cash value (where applicable)',
    ],
  },
  netPersonalAssets: {
    title: 'Valid Supporting Documents (Issued within the last 3 months)',
    items: [
      'Latest bank statements or CPF statement',
      'Latest CDP or brokerage statements',
      'Property title documents and ownership details',
      'Latest mortgage statement (if applicable)',
      'Recent bank valuation, government assessment, purchase agreement or other acceptable property valuation evidence (where applicable)',
      'Walton investment statements US and CA (where applicable, free from mortgage)',
    ],
  },
};

const ClientAccreditation = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(userInfo);
  const previewDetail = JSON.parse(sessionStorage.getItem('previewDetail'));
  const isIR = user.sfUserType === 'IR';
  const [formValues, setFormValues] = useState({
    optInProcess: '',
    annualIncome: '',
    netPersonalAssets: '',
    financialAssets: '',
  });
  const [supportingDocuments, setSupportingDocuments] = useState({});
  const [axDocuments, setAxDocuments] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [warningAgree, setWarningAgree] = useState(false);
  const [disclaimerAgree, setDisclaimerAgree] = useState(false);
  const [consentAgree, setConsentAgree] = useState(false);
  const signatureCanvasRef = useRef(null);
  const [signatureImage, setSignatureImage] = useState('');
  const [signatureError, setSignatureError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalProperty, setModalProperty] = useState({ isModalOpen: false, icon: '', title: '', content: '' });
  const [contentList, setContentList] = useState([]);
  const [complianceComment, setComplianceComment] = useState('');
  const [aiDecision, setAiDecision] = useState(user.sfUserType === 'CL' ? user.aiDecision : previewDetail?.aiDecision);
  const [downloadingQuestion, setDownloadingQuestion] = useState(null);

  useEffect(() => {
    const getContent = async () => {
      setLoading(true);
      try {
        const data = await get('/clientAccreditation/content', {
          accessToken: user.accessToken,
          userId: user.userId,
        });
        if (Array.isArray(data)) setContentList(data);
      } finally {
        setLoading(false);
      }
    };
    getContent();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getContentTextByField = (field) => {
    const item = contentList.find((entry) => entry.field === field);
    if (!item) return '';
    switch (window.localStorage.lang) {
      case 'ZH_CN':
      case 'zh_CN':
      case 'Simplified Chinese':
        return item.contentsc || '';
      case 'ZH_HK':
      case 'zh_HK':
      case 'Traditional Chinese':
        return item.contenttc || '';
      case 'JP':
      case 'jp':
      case 'Japanese':
        return item.contentja || '';
      default:
        return item.contenten || '';
    }
  };

  const isOptIn = formValues.optInProcess === 'Opt In';

  const warningContent = isOptIn ? getContentTextByField('Opt In - Warning') : '';
  const disclaimerContent = isOptIn ? getContentTextByField('Opt In - Disclaimer') : '';
  const consentContent = isOptIn ? getContentTextByField('Opt In - Consent') : '';

  useEffect(() => {
    if (!user.sfUserType) return;
    const pathname = history.location.pathname.toLowerCase();
    const sfUserType = previewDetail !== null ? previewDetail?.sfUserType : user.sfUserType;
    const isClientView = user.sfUserType === 'CL'; //sfUserType === 'CL' || user.sfUserType === 'CL' || previewDetail?.sfUserType === 'CL';
    const isSGRegion = user.sfUserType === 'IR' ? previewDetail?.country === 'Singapore' : user.country === 'Singapore';

    if (!isClientView || !isSGRegion) {
      history.push({ pathname: user.sfUserType === 'IR' ? '/login-as/clients' : '/home' });
      return;
    }

    if (user.sfUserType === 'CL' && pathname.includes('preview')) {
      history.push({ pathname: '/clientAccreditation' });
    } else if (user.sfUserType === 'IR' && pathname === '/clientaccreditation') {
      history.push({ pathname: '/preview/clientAccreditation' });
    }
  }, [history, user.sfUserType, user.country, previewDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchDetail = async () => {
      setLoadingDetail(true);
      try {
        const data = await get('/clientAccreditation/getDetail', {
          userId: user.userId,
          accessToken: user.accessToken,
        });

        if (data) {
          if (data.complianceComment) setComplianceComment(data.complianceComment);
          if (data.aiDecision !== undefined) setAiDecision(data.aiDecision);

          const hasFormData = data.optInProcess || data.annualIncome || data.netPersonalAssets || data.financialAssets;
          if (hasFormData) {
            const toYesNo = (val) => {
              if (val === 1 || val === '1') return 'Yes';
              if (val === 2 || val === '2') return 'No';
              return '';
            };
            setFormValues({
              optInProcess: data.optInProcess || '',
              annualIncome: toYesNo(data.annualIncome),
              netPersonalAssets: toYesNo(data.netPersonalAssets),
              financialAssets: toYesNo(data.financialAssets),
            });
          }

          if (Array.isArray(data.supportingDocuments) && data.supportingDocuments.length) {
            const docs = {};
            data.supportingDocuments.forEach((item) => {
              if (!docs[item.questionName]) docs[item.questionName] = [];
              // Entries without a fileId are AX-stored docs (already merged and sent to AX on a previous submit)
              const isAxStored = !item.fileId;
              docs[item.questionName].push({
                _id: isAxStored ? `ax-${item.questionName}` : `existing-${item.fileId}`,
                name: item.fileName,
                file: null,
                fileId: item.fileId || null,
                axStored: isAxStored,
                uploading: false,
                error: null,
              });
            });
            setSupportingDocuments(docs);
            setAxDocuments(docs);
          }
        }

        // Local answers saved before navigating to Products take priority over server data,
        // since they may not have been submitted yet.
        const savedFormValues = sessionStorage.getItem('clientAccreditationFormValues');
        if (savedFormValues) {
          try {
            const saved = JSON.parse(savedFormValues);
            setFormValues(saved.formValues);
            // The user already agreed to these before navigating away, so don't make them re-check.
            setWarningAgree(Boolean(saved.warningAgree));
            setDisclaimerAgree(Boolean(saved.disclaimerAgree));
            setConsentAgree(Boolean(saved.consentAgree));
          } catch (e) {
            // ignore malformed saved state
          }
          sessionStorage.removeItem('clientAccreditationFormValues');
        }
      } catch (e) {
        // silently ignore — form still usable without pre-loaded data
      } finally {
        setLoadingDetail(false);
      }
    };
    fetchDetail();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const optInQuestion = {
    name: 'optInProcess',
    title: 'Accreditation Status:',
    description: 'Would you like to proceed with the Accredited Investor Assessment below?',
    options: [
      { value: 'Opt In', label: 'Yes, I would like to Opt in and be assessed as an Accredited Investor.' },
      { value: 'NA', label: 'No, I do not wish to be assessed as an Accredited Investor.' },
    ],
  };

  const accreditationQuestions = [
    {
      name: 'annualIncome',
      title: 'Annual Personal Income',
      description: 'My income in the last 12 months was at least S$300,000 (or equivalent in foreign currency), based on employment income, business income and/or other sources as recognised under MAS guidance.',
    },
    {
      name: 'financialAssets',
      title: 'Financial Assets',
      description: 'I estimate that my financial assets, net of any related borrowing (e.g. margin loans), are at least S$1,000,000. Financial assets may include, for example, bank deposits, listed securities, investment funds and similar instruments, as determined under MAS rules.',
    },
    {
      name: 'netPersonalAssets',
      title: 'Net Personal Assets',
      description: 'I estimate that the total value of my personal assets minus my liabilities is at least S$2,000,000. I understand that, under MAS rules, the net equity in my primary residence can count towards this amount but is generally capped at S$1,000,000.',
    },
  ];

  const handleOptInChange = (value) => {
    // Only the optInProcess answer changes here — eligibility answers, joint account details,
    // and supporting documents are kept so switching back to Opt In restores what was entered.
    setFormValues((prevValues) => ({
      ...prevValues,
      optInProcess: value,
    }));
    // Warning/Disclaimer/Consent content and the signature requirement differ between Opt In and
    // Opt Out, so agreements and the signature must be given again for whichever path is selected.
    setWarningAgree(false);
    setDisclaimerAgree(false);
    setConsentAgree(false);
    setSignatureImage('');
    setSignatureError('');
    signatureCanvasRef.current?.clear();
  };

  const handleQuestionSelect = (name) => {
    setFormValues((prevValues) => {
      const updated = { ...prevValues };
      accreditationQuestions.forEach((question) => {
        updated[question.name] = question.name === name ? 'Yes' : 'No';
      });
      return updated;
    });

    const clearedNames = accreditationQuestions.map((question) => question.name).filter((n) => n !== name);
    setSupportingDocuments((prev) => {
      const updated = { ...prev };
      clearedNames.forEach((n) => { updated[n] = axDocuments[n] || []; });
      return updated;
    });
    setFileErrors((prev) => {
      const updated = { ...prev };
      clearedNames.forEach((n) => { updated[n] = null; });
      return updated;
    });
  };

  const applyDocumentUpload = (name, newFiles, replace) => {
    setFileErrors((prev) => ({ ...prev, [name]: null }));
    const newEntries = newFiles.map((file) => ({
      _id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      name: file.name,
      file,
      fileId: null,
      uploading: false,
      error: null,
    }));
    setSupportingDocuments((prev) => ({
      ...prev,
      [name]: replace ? newEntries : [...(prev[name] || []), ...newEntries],
    }));
  };

  const handleDocumentUpload = (name, event) => {
    if (isIR) return;
    const newFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (!newFiles.length) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const invalidType = newFiles.find((f) => !allowedTypes.includes(f.type));
    if (invalidType) {
      setFileErrors((prev) => ({ ...prev, [name]: 'Only PDF and image files (JPG, PNG) are allowed.' }));
      return;
    }

    const oversized = newFiles.filter((f) => f.size > 10 * 1024 * 1024);
    if (oversized.length) {
      setFileErrors((prev) => ({ ...prev, [name]: 'Each file must not exceed 10MB.' }));
      return;
    }

    const existingEntries = supportingDocuments[name] || [];
    const hasAxStored = existingEntries.some((e) => e.axStored);
    const nonAxEntries = existingEntries.filter((e) => !e.axStored);

    if (!hasAxStored && nonAxEntries.length + newFiles.length > 15) {
      setFileErrors((prev) => ({ ...prev, [name]: 'Maximum 15 files allowed per item.' }));
      return;
    }

    const duplicates = newFiles.filter((f, i) =>
      nonAxEntries.some((e) => e.name === f.name) || newFiles.findIndex((x) => x.name === f.name) !== i
    );
    if (duplicates.length) {
      setFileErrors((prev) => ({ ...prev, [name]: 'Duplicate file(s) are not allowed.' }));
      return;
    }

    if (hasAxStored) {
      Modal.confirm({
        title: 'Replace existing document?',
        content: 'Uploading a new file will replace the current document. Do you want to continue?',
        okText: 'Replace',
        cancelText: 'Cancel',
        onOk: () => applyDocumentUpload(name, newFiles, true),
      });
      return;
    }

    applyDocumentUpload(name, newFiles, false);
  };

  const handleDocumentPreview = async (questionName) => {
    const previewWindow = window.open('', '_blank');
    setDownloadingQuestion(questionName);
    try {
      const data = await get('/clientAccreditation/getDocument', {
        questionName,
        accessToken: user.accessToken,
        userId: user.userId,
      });
      if (!data || !data.base64Content) {
        previewWindow.close();
        return;
      }
      const byteArray = Uint8Array.from(atob(data.base64Content), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      previewWindow.location.href = url;
    } catch (e) {
      previewWindow.close();
    } finally {
      setDownloadingQuestion(null);
    }
  };

  const handleDocumentRemove = (name, fileIndex) => {
    setSupportingDocuments((prev) => {
      const updated = (prev[name] || []).filter((_, i) => i !== fileIndex);
      return {
        ...prev,
        [name]: updated.length ? updated : (axDocuments[name] || []),
      };
    });
    setFileErrors((prev) => ({ ...prev, [name]: null }));
  };

  const getSignatureBounds = (strokes) => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    strokes.forEach((stroke) => {
      stroke.forEach((point) => {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.y > maxY) maxY = point.y;
      });
    });

    return { minX, maxX, minY, maxY };
  };

  const handleSignatureClear = () => {
    signatureCanvasRef.current.clear();
    setSignatureImage('');
    setSignatureError('');
  };

  const handleSignatureEnd = () => {
    const strokes = signatureCanvasRef.current?.toData();
    const { minX, maxX, minY, maxY } = getSignatureBounds(strokes);
    const height = maxY - minY;
    const width = maxX - minX;

    if (height > 130 && width > 20 && !signatureCanvasRef.current?.isEmpty()) {
      setSignatureError('');
      setSignatureImage(signatureCanvasRef.current.getCanvas().toDataURL('image/png').split(',')[1]);
    } else {
      setSignatureError('The signature does not meet the minimum size requirement. Kindly provide a larger signature.');
      setSignatureImage('');
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const hasOptInAnswer = Boolean(formValues.optInProcess);
    const hasRequiredAnswers = !isOptIn || accreditationQuestions.some((question) => formValues[question.name] === 'Yes');
    const isComplete = hasOptInAnswer && hasRequiredAnswers && (!isOptIn || hasRequiredDocuments);

    if (!isComplete || !requiredAgreementsMet || (isOptIn && !signatureImage) || submitting) return;

    setLoading(true);
    setSubmitting(true);

    try {
      const toApiValue = (val) => (isOptIn && val === 'Yes' ? 1 : 0);
      const formData = new FormData();
      formData.append('optInProcess', formValues.optInProcess);
      formData.append('annualIncome', toApiValue(formValues.annualIncome));
      formData.append('netPersonalAssets', toApiValue(formValues.netPersonalAssets));
      formData.append('financialAssets', toApiValue(formValues.financialAssets));
      formData.append('signatureFile', signatureImage);

      for (const question of (isOptIn ? accreditationQuestions : [])) {
        for (const entry of (supportingDocuments[question.name] || [])) {
          if (!entry.axStored && entry.file) {
            const paramName = `files${question.name.charAt(0).toUpperCase()}${question.name.slice(1)}`;
            formData.append(paramName, entry.file, entry.name);
          }
        }
      }

      const data = await postForm('/clientAccreditation/submit', formData, {
        userId: user.userId,
        accessToken: user.accessToken,
      });


      if (data) {
        const userUpdates = {};
        if (data.aiDecision !== undefined) userUpdates.aiDecision = data.aiDecision;
        if (data.isClientAccredited !== undefined) userUpdates.isClientAccredited = data.isClientAccredited;
        if (data.optInProcess !== undefined) userUpdates.optInProcess = data.optInProcess;
        if (Object.keys(userUpdates).length) {
          dispatch(updateUserInfo({ ...user, ...userUpdates }));
        }
        if (data.complianceComment !== undefined) setComplianceComment(data.complianceComment);
        if (data.aiDecision !== undefined) setAiDecision(data.aiDecision);

        const uploadedDocNames = data.uploadedDocNames || {};
        // Questions that had a newly-selected file included in this submit
        const attemptedQuestions = accreditationQuestions
          .filter((question) => (supportingDocuments[question.name] || []).some((entry) => !entry.axStored && entry.file));

        if (attemptedQuestions.length) {
          const newAxEntries = {};
          attemptedQuestions.forEach((question) => {
            const fileName = uploadedDocNames[question.name];
            if (fileName) {
              newAxEntries[question.name] = [{
                _id: `ax-${question.name}`,
                name: fileName,
                file: null,
                fileId: null,
                axStored: true,
                uploading: false,
                error: null,
              }];
            }
          });

          // Replace newly-uploaded entries with axStored entries so the UI reflects what's in AX;
          // an attempted question missing from uploadedDocNames didn't persist, so clear it and
          // require the user to upload it again.
          setSupportingDocuments((prev) => {
            const updated = { ...prev, ...newAxEntries };
            attemptedQuestions.forEach((question) => {
              if (!uploadedDocNames[question.name]) updated[question.name] = axDocuments[question.name] || [];
            });
            return updated;
          });
          if (Object.keys(newAxEntries).length) {
            setAxDocuments((prev) => ({ ...prev, ...newAxEntries }));
          }
        }

        if (isOptIn && uploadedDocNames.optInProcess) {
          const optInEntry = [{
            _id: 'ax-optInProcess',
            name: uploadedDocNames.optInProcess,
            file: null,
            fileId: null,
            axStored: true,
            uploading: false,
            error: null,
          }];
          setSupportingDocuments((prev) => ({ ...prev, optInProcess: optInEntry }));
          setAxDocuments((prev) => ({ ...prev, optInProcess: optInEntry }));
        } else if (!isOptIn) {
          // Opting out clears the opt-in form and all eligibility docs on the server, so drop the
          // stale entries left over from a previous Opt In submission.
          setSupportingDocuments((prev) => ({ ...prev, optInProcess: [] }));
          setAxDocuments((prev) => ({ ...prev, optInProcess: [] }));
        }

        const questionsToClearDocs = accreditationQuestions
          .filter((question) => !isOptIn || formValues[question.name] === 'No');

        if (questionsToClearDocs.length) {
          setSupportingDocuments((prev) => {
            const updated = { ...prev };
            questionsToClearDocs.forEach((question) => { updated[question.name] = []; });
            return updated;
          });
          setAxDocuments((prev) => {
            const updated = { ...prev };
            questionsToClearDocs.forEach((question) => { updated[question.name] = []; });
            return updated;
          });
        }

        if (!isOptIn) {
          // Opting out clears the eligibility answers on the server, so the locally-held answers
          // from a previous Opt In submission shouldn't be shown as still selected.
          setFormValues((prevValues) => ({
            ...prevValues,
            annualIncome: '',
            netPersonalAssets: '',
            financialAssets: '',
          }));
        }

        setWarningAgree(false);
        setDisclaimerAgree(false);
        setConsentAgree(false);
        signatureCanvasRef.current?.clear();
        setSignatureImage('');
        setModalProperty({ isModalOpen: true, icon: 'submit-success', title: 'Submission Successful', content: 'Client accreditation form completed.' });
      } else {
        setModalProperty({ isModalOpen: true, icon: 'submit-error', title: 'ERROR', content: 'Error in submission. Please try again.' });
      }
    } catch (error) {
      setModalProperty({ isModalOpen: true, icon: 'submit-error', title: 'ERROR', content: 'Error in submission. Please try again.' });
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const hasQualifyingAnswer =
    formValues.annualIncome === 'Yes' ||
    formValues.netPersonalAssets === 'Yes' ||
    formValues.financialAssets === 'Yes';

  const questionsNeedingDocs = accreditationQuestions.filter((q) => formValues[q.name] === 'Yes');
  const hasRequiredDocuments = questionsNeedingDocs.every(
    (q) => (supportingDocuments[q.name] || []).some((f) => (f.fileId && !f.error) || f.file || f.axStored)
  );

  const requiredAgreementsMet = !isOptIn || (warningAgree && disclaimerAgree && consentAgree);

  const showOpportunitiesBanner = isOptIn && (
    hasQualifyingAnswer || aiDecision === 'Yes'
  );

  const showComplianceComment = isOptIn && Boolean(complianceComment) && aiDecision === 'No';

  return (
    <div className="client-accreditation">
      <div className="main-box">
        {showOpportunitiesBanner && (
          <div className="opportunities-banner">
            <div className="opportunities-banner-inner">
              <div className="opportunities-banner-copy">
                <p className="opportunities-banner-title">Congratulations! An Exclusive Opportunity Awaits You</p>
                <div className="opportunities-banner-checks">
                  <span>✅ Earn 10% p.a.</span>
                  <span>✅ 3+1+1 Year Tenure</span>
                  <span>✅ Partnered with One of the Largest Home Builders in the USA</span>
                </div>
                <p className="opportunities-banner-sub">Secure your position today and be part of a proven growth story.</p>
              </div>
              <button type="button" className="opportunities-btn" onClick={() => { sessionStorage.setItem('clientAccreditationFormValues', JSON.stringify({ formValues, warningAgree, disclaimerAgree, consentAgree })); history.push({ pathname: isIR ? '/preview/products' : '/products', state: { fromAccreditation: true } }); }}>
                View Exciting Opportunities
              </button>
            </div>
          </div>
        )}
        
        <div className="headline client-accreditation-wrapper">
          <div className="col-md-4 client-accreditation-title">
            <p>Client Accreditation</p>
          </div>
          <div className="col-md-8 client-accreditation-header"></div>
        </div>

        <form className="client-accreditation-form" onSubmit={handleSubmit}>
          <fieldset className="accreditation-question">
            <legend>{optInQuestion.title}</legend>
            <p>{optInQuestion.description}</p>
            <div className="accreditation-options">
              {optInQuestion.options.map((option) => (
                <label
                  className={`accreditation-option${formValues.optInProcess === option.value ? ' is-selected' : ''}`}
                  key={option.value}
                >
                  <input
                    type="radio"
                    name={optInQuestion.name}
                    checked={formValues.optInProcess === option.value}
                    onChange={() => handleOptInChange(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {isOptIn && (supportingDocuments.optInProcess || []).some((f) => f.axStored) && (
              <ul className="upload-list">
                <li>
                  <span className="upload-file-name">
                    {(supportingDocuments.optInProcess || []).find((f) => f.axStored)?.name}
                  </span>
                  <button
                    className="upload-view-button"
                    disabled={downloadingQuestion === 'optInProcess'}
                    onClick={() => handleDocumentPreview('optInProcess')}
                    type="button"
                  >
                    {downloadingQuestion === 'optInProcess' ? <LoadingOutlined spin /> : 'View'}
                  </button>
                </li>
              </ul>
            )}
          </fieldset>

          {(isOptIn && (warningContent || !isIR)) ? (
            <div className="accreditation-intro">
              {warningContent ? (
                <div className="accreditation-intro-content" dangerouslySetInnerHTML={{ __html: warningContent }} />
              ) : null}

              {!isIR && (
                <div className="accreditation-declaration">
                  <div className="acknowledge-section">
                    <div className="agree">
                      {warningAgree ? (
                        <svg onClick={() => setWarningAgree(false)} width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <ellipse cx="11.3514" cy="12" rx="11.3514" ry="12" fill="#FFC81C" />
                          <path d="M17.71 8.31595C17.3462 7.91041 16.7396 7.89255 16.3554 8.27813L10.2381 14.411L7.32038 11.2441C6.95083 10.8433 6.34493 10.8345 5.96546 11.2252C5.58631 11.6156 5.57833 12.2568 5.94755 12.6576L9.5249 16.5403C9.71237 16.7439 9.96152 16.8466 10.2113 16.8466C10.4483 16.8466 10.6853 16.754 10.8709 16.5687L17.6743 9.748C18.0582 9.36305 18.0744 8.72184 17.71 8.31595Z" fill="#003448" />
                        </svg>
                      ) : (
                        <svg onClick={() => setWarningAgree(true)} width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <ellipse cx="11.3514" cy="12" rx="11.3514" ry="12" fill="#FFC81C" />
                        </svg>
                      )}
                      <p>I have read and understood the warning above.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {(isOptIn && (disclaimerContent || !isIR)) ? (
            <div className="accreditation-intro">
              {disclaimerContent ? (
                <div className="accreditation-intro-content" dangerouslySetInnerHTML={{ __html: disclaimerContent }} />
              ) : null}

              {!isIR && (
                <div className="accreditation-declaration">
                  <div className="acknowledge-section">
                    <div className="agree">
                      {disclaimerAgree ? (
                        <svg onClick={() => setDisclaimerAgree(false)} width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <ellipse cx="11.3514" cy="12" rx="11.3514" ry="12" fill="#FFC81C" />
                          <path d="M17.71 8.31595C17.3462 7.91041 16.7396 7.89255 16.3554 8.27813L10.2381 14.411L7.32038 11.2441C6.95083 10.8433 6.34493 10.8345 5.96546 11.2252C5.58631 11.6156 5.57833 12.2568 5.94755 12.6576L9.5249 16.5403C9.71237 16.7439 9.96152 16.8466 10.2113 16.8466C10.4483 16.8466 10.6853 16.754 10.8709 16.5687L17.6743 9.748C18.0582 9.36305 18.0744 8.72184 17.71 8.31595Z" fill="#003448" />
                        </svg>
                      ) : (
                        <svg onClick={() => setDisclaimerAgree(true)} width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <ellipse cx="11.3514" cy="12" rx="11.3514" ry="12" fill="#FFC81C" />
                        </svg>
                      )}
                      <p>I acknowledge and agree to the disclaimer above.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {(isOptIn && (consentContent || !isIR)) ? (
            <div className="accreditation-intro">
              {consentContent ? (
                <div className="accreditation-intro-content" dangerouslySetInnerHTML={{ __html: consentContent }} />
              ) : null}

              {!isIR && (
                <div className="accreditation-declaration">
                  <div className="acknowledge-section">
                    <div className="agree">
                      {consentAgree ? (
                        <svg onClick={() => setConsentAgree(false)} width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <ellipse cx="11.3514" cy="12" rx="11.3514" ry="12" fill="#FFC81C" />
                          <path d="M17.71 8.31595C17.3462 7.91041 16.7396 7.89255 16.3554 8.27813L10.2381 14.411L7.32038 11.2441C6.95083 10.8433 6.34493 10.8345 5.96546 11.2252C5.58631 11.6156 5.57833 12.2568 5.94755 12.6576L9.5249 16.5403C9.71237 16.7439 9.96152 16.8466 10.2113 16.8466C10.4483 16.8466 10.6853 16.754 10.8709 16.5687L17.6743 9.748C18.0582 9.36305 18.0744 8.72184 17.71 8.31595Z" fill="#003448" />
                        </svg>
                      ) : (
                        <svg onClick={() => setConsentAgree(true)} width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <ellipse cx="11.3514" cy="12" rx="11.3514" ry="12" fill="#FFC81C" />
                        </svg>
                      )}
                      <p>I acknowledge and agree to the statements in this section.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {showComplianceComment ? (
            <div className="compliance-comment">
              <p className="compliance-comment-label">Compliance Comments</p>
              <p className="compliance-comment-text">{complianceComment}</p>
            </div>
          ) : null}

          {isOptIn && <p className="accreditation-questions-title">Eligibility Basis (Select one only)</p>}

          {isOptIn && accreditationQuestions.map((question, index) => (
            <fieldset
              className="accreditation-question"
              disabled={!(warningAgree && disclaimerAgree && consentAgree)}
              key={question.name}
            >
              <legend>
                <span>{index + 1}.</span>
                {question.title}
              </legend>
              <div className="accreditation-options">
                <label
                  className={`accreditation-option${formValues[question.name] === 'Yes' ? ' is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="eligibilityBasis"
                    checked={formValues[question.name] === 'Yes'}
                    onChange={() => handleQuestionSelect(question.name)}
                  />
                  <span>{question.description}</span>
                </label>
              </div>
              {SHOW_SUPPORTING_DOCUMENTS && formValues[question.name] === 'Yes' && (
                <div className={`accreditation-upload${!(supportingDocuments[question.name] || []).some((f) => (f.fileId && !f.error) || f.file || f.axStored) ? ' is-missing' : ''}`}>
                  <div className="upload-header">
                    <div>
                      <p className="upload-title">
                        Supporting Documents
                        {SUPPORTING_DOCUMENTS_INFO[question.name] && (
                          <Tooltip
                            title={(
                              <div className="upload-title-tooltip">
                                <p>{SUPPORTING_DOCUMENTS_INFO[question.name].title}</p>
                                <ul>
                                  {SUPPORTING_DOCUMENTS_INFO[question.name].items.map((item) => (
                                    <li key={item}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          >
                            <InfoCircleOutlined className="upload-title-info" />
                          </Tooltip>
                        )}
                      </p>
                      <p className="upload-hint">A supporting document is required for this answer. Please upload at least one file (PDF, JPG, or PNG, up to 15 files, max 10MB each).</p>
                    </div>
                    <input
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="upload-input"
                      id={`supporting-documents-${question.name}`}
                      multiple
                      onChange={(event) => handleDocumentUpload(question.name, event)}
                      type="file"
                    />
                    <label className="upload-button" htmlFor={`supporting-documents-${question.name}`}>
                      <UploadOutlined />
                      Upload
                    </label>
                  </div>
                  {!(supportingDocuments[question.name] || []).some((f) => (f.fileId && !f.error) || f.file || f.axStored) && (
                    <p className="upload-required-notice">
                      Please upload a supporting document above. This is required before you can submit.
                    </p>
                  )}
                  {(supportingDocuments[question.name] || []).length ? (
                    <ul className="upload-list">
                      {supportingDocuments[question.name].map((entry, fileIndex) => (
                        <li key={entry._id} className={entry.uploading ? 'is-uploading' : entry.error ? 'has-error' : ''}>
                          {entry.uploading ? <LoadingOutlined className="upload-status-icon" spin /> : null}
                          <span className="upload-file-name">{entry.name}</span>
                          {entry.axStored && (
                            <button
                              className="upload-view-button"
                              disabled={downloadingQuestion === question.name}
                              onClick={() => handleDocumentPreview(question.name)}
                              type="button"
                            >
                              {downloadingQuestion === question.name ? <LoadingOutlined spin /> : 'View'}
                            </button>
                          )}
                          {entry.error ? <span className="upload-file-error">{entry.error}</span> : null}
                          {!entry.axStored && (
                            <button
                              aria-label="Remove document"
                              disabled={entry.uploading}
                              onClick={() => handleDocumentRemove(question.name, fileIndex)}
                              type="button"
                            >
                              <CloseOutlined />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {fileErrors[question.name] ? (
                    <p className="upload-error">{fileErrors[question.name]}</p>
                  ) : null}
                </div>
              )}
            </fieldset>
          ))}

          {!isIR && isOptIn && (
            <div className={`signature-section${!requiredAgreementsMet ? ' is-disabled' : ''}`}>
              <p className="signature-title">
                Client Declaration and Signature <i className="required">*</i>
              </p>
              <p className="signature-title signature-note">
                By signing below, I confirm that I have read this form and that the information provided is true, complete and accurate.
              </p>
              <div className="signature-container">
                <div className="signature-div">
                  <SignatureCanvas
                    penColor="black"
                    ref={signatureCanvasRef}
                    canvasProps={{ className: 'sigCanvas' }}
                    onEnd={handleSignatureEnd}
                    onBegin={() => setSignatureError('')}
                    minWidth={2.5}
                    maxWidth={4}
                  />
                </div>
                <div className="clear-button-div">
                  <span onClick={() => requiredAgreementsMet && handleSignatureClear()}>Clear</span>
                </div>
                {signatureError !== '' && (
                  <p className="signature-error">{signatureError}</p>
                )}
              </div>
            </div>
          )}

          {!isIR && (
            <>
              <div className="accreditation-action">
                <button
                  type="button"
                  className="btn btn-outline-next"
                  disabled={
                    submitting ||
                    !formValues.optInProcess ||
                    (isOptIn && !accreditationQuestions.some((question) => formValues[question.name] === 'Yes')) ||
                    (isOptIn && !hasRequiredDocuments) ||
                    !requiredAgreementsMet ||
                    (isOptIn && !signatureImage)
                  }
                  onClick={handleSubmit}
                >
                  SUBMIT
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      {(loading || loadingDetail) && <Loading />}

      <SubmitSuccessPopUp
        isModalOpen={modalProperty.isModalOpen}
        icon={modalProperty.icon}
        title={modalProperty.title}
        content={modalProperty.content}
        handleOk={() => setModalProperty((prev) => ({ ...prev, isModalOpen: false }))}
        handleCancel={() => setModalProperty((prev) => ({ ...prev, isModalOpen: false }))}
      />
    </div>
  );
};

export default ClientAccreditation;
